/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { AccentPanel } from '@/shared/ui/components/composed/accent-panel';
import type { SettingsPendingItem } from '../utils/build-settings-pending';
import type { SettingsSection } from '../constants/settings-nav';

interface SettingsPendingBandProps {
  items: SettingsPendingItem[];
  onNavigate: (section: SettingsSection) => void;
}

/**
 * The band above the tabs: what is still unconfigured, and a shortcut to it.
 *
 * This is the part of the redesign that changes what the screen knows rather
 * than how it looks. Before it, a blank CNPJ and a portal with its sending
 * switched off were invisible until someone happened to open the right tab.
 *
 * Renders nothing when there is nothing pending — an empty band congratulating
 * the user on having no pendencies is a row of chrome that never goes away.
 *
 * @param props - Component props.
 * @returns The band, or null when nothing is pending.
 */
export function SettingsPendingBand({ items, onNavigate }: SettingsPendingBandProps) {
  const { t } = useTranslation('features/agency-settings');
  if (items.length === 0) return null;

  return (
    // A rule rather than a tinted box: the band reports a handful of small
    // gaps, and a filled amber panel makes them read as an alarm every time
    // the page opens. The rule sits inside the card — as a border it bent
    // around the radius and ate the card's own left edge.
    <AccentPanel accent="var(--accent-mostarda)" testId="settings-pending-band">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Text
          component="span"
          style={{ fontSize: 'var(--text-body-sm)', fontWeight: 700, color: 'var(--accent-mostarda)' }}
        >
          {t('v2.pending.title', { count: items.length })}
        </Text>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {items.map((item) => (
            <UnstyledButton
              key={item.key}
              onClick={() => onNavigate(item.section)}
              data-testid={`settings-pending-chip-${item.key}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, height: 26,
                padding: '0 var(--space-2-5)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--surface-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: 'var(--text-meta)',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </UnstyledButton>
          ))}
        </div>
      </div>
    </AccentPanel>
  );
}
